// eslint-disable-next-line no-restricted-imports
import { useBreakpointComponent } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import { PageHeader } from "#/features/global-ui/page-header"
import React from "react"
import { FormattedDate, FormattedMessage, useIntl } from "react-intl"
import { getPiiProps } from "#/services/tracking"
import { BackIcon, Box, IconButton, Stack, Typography, Divider, useTheme } from "@carrotfertility/carotene-core"

type BackArrowButtonProps = {
  onClick: () => void
}

const BackArrowButton = ({ onClick }: BackArrowButtonProps): JSX.Element => {
  const intl = useIntl()

  return (
    <IconButton
      aria-label={intl.formatMessage({ defaultMessage: "back", description: "go back to the previous page" })}
      onClick={onClick}
      size="small"
      sx={(theme) => ({
        bgcolor: theme.palette.primary.light,
        borderRadius: theme.tokens.borderRadius.round,
        color: theme.palette.primary.main
      })}
    >
      <BackIcon />
    </IconButton>
  )
}

const Headers = (): JSX.Element => {
  const { showNewThreadForm, setShowNewThreadForm, setShowOnlyThreadsList, selectedThread } = useInAppMessaging()
  const theme = useTheme()

  return (
    <Stack display="flex" direction="column" spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <Stack display="flex" direction="row" spacing={theme.spacing(theme.tokens.spacing.md)} alignItems="center">
        <BackArrowButton
          onClick={() => {
            if (showNewThreadForm) {
              setShowNewThreadForm(false)
              setShowOnlyThreadsList(true)
            } else {
              setShowOnlyThreadsList(true)
            }
          }}
        />
        {showNewThreadForm ? (
          <Box display="flex" alignItems="end">
            <Typography variant="h5" component="h3" marginInlineStart={(theme) => theme.tokens.spacing.sm}>
              <FormattedMessage defaultMessage="Send a new message" />
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing="none" sx={{ marginInlineStart: (theme) => theme.tokens.spacing.md }}>
              <Typography variant="overline" color={(theme) => theme.palette.text.disabled}>
                {selectedThread.threadId !== -1 && (
                  <FormattedDate month="short" day="numeric" value={selectedThread.updatedAt} />
                )}
              </Typography>
              <Typography variant="h5" component="h3" {...getPiiProps()}>
                {selectedThread.subject}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
      <Divider
        sx={{
          borderColor: (theme) => theme.palette.border.light
        }}
      />
    </Stack>
  )
}

const MessagesHeader = ({
  titleOnly = false,
  hideDivider = false
}: {
  /**
   * Render the plain page title and nothing else, whatever the Carrot selection is.
   *
   * Set by `MessagesShell` on the Providers tab. Everything below reads the CARROT thread out of
   * `InAppMessagingContext`, so on that tab this component would head the page with a conversation
   * from the other one — the two tabs' selections are deliberately independent, and the page header
   * sits above both of them.
   */
  titleOnly?: boolean
  /**
   * Drop the rule under the PAGE TITLE, because the tab bar below it draws one already.
   *
   * Forwarded to both `PageHeader` paths and to neither `Headers` path, and the asymmetry is the whole
   * point: `PageHeader` renders the page title, which is what the tab bar sits directly beneath.
   * `Headers` renders the narrow-viewport THREAD chrome — a back button and the open thread's subject —
   * and its own divider separates that chrome from what follows it, a job nothing else is doing. So
   * this is not "hide the messages divider", it is "the page title's rule is supplied elsewhere".
   */
  hideDivider?: boolean
} = {}): JSX.Element => {
  const { showOnlyThreadsList, selectedThread } = useInAppMessaging()
  const intl = useIntl()
  const title = intl.formatMessage({ defaultMessage: "Messages", description: "contact us through messages" })
  const header = useBreakpointComponent([
    <Headers key="smallest" />,
    <Headers key="small" />,
    null,
    <PageHeader key="large" pageTitle={title} hideDivider={hideDivider} />
  ])

  // `selectedThread == null` is now reachable on the FIRST render: a URL that names a thread opens the
  // thread pane straight away, while the threads request is still in flight and there is nothing to
  // select yet. `Headers` reads `selectedThread.threadId` and `.subject` unguarded, so it must not be
  // reached before there is a thread.
  //
  // NOT a narrow-viewport guard, which is what it looks like. `useBreakpointComponent` seeds its
  // breakpoint index to 0 and only measures in an effect (@theme-ui/match-media), so the first render
  // at EVERY width takes a `Headers` branch and corrects itself afterwards. Before this guard a deep
  // link crashed on a desktop too.
  if (titleOnly || showOnlyThreadsList || selectedThread == null) {
    return <PageHeader pageTitle={title} hideDivider={hideDivider} />
  }

  return header as JSX.Element
}

export { MessagesHeader }
